import React, { useState, useEffect, useRef } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StreamingResponse } from '@/components/tools/StreamingResponse';
import { useTools } from '@/hooks/useTools';
import { useToolExecution } from '@/hooks/useToolExecution';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useWorkspaceDetail } from '@/hooks/useWorkspaceDetail';
import { ArrowLeft, Play } from 'lucide-react';
import { AskTheCountyUI } from '../_components/AskTheCountyUI';
import { PlatformGuideUI } from '../_components/PlatformGuideUI';
import { DocumentQaUI } from '../_components/DocumentQaUI';
import { MeetingSummarizerUI } from '../_components/MeetingSummarizerUI';
import { TopicMonitorUI } from '../_components/TopicMonitorUI';
import { DeepResearchBriefUI } from '../_components/DeepResearchBriefUI';
import { FullTextSearchUI } from '../_components/FullTextSearchUI';
import { MessageActions } from '../_components/MessageActions';
import { apiFetch } from '@/lib/apiFetch';
import { usePageTitle } from '@/hooks/usePageTitle';

// ── Slug → dedicated UI ───────────────────────────────────────────────────────
const DEDICATED_SLUGS = new Set([
  'on-the-record',
  'platform-guide',
  'document-qa',
  'meeting-summary',
  'topic-monitor',
  'deep-research-brief',
  'full-text-search',
]);

export const ToolPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // Standardized feedback for generic runner responses (e.g. Meeting Digest).
  const [runnerReasons, setRunnerReasons] = useState<{
    up: { code: string; label: string }[];
    down: { code: string; label: string }[];
  }>({ up: [], down: [] });

  useEffect(() => {
    apiFetch('/api/feedback/reasons')
      .then((r) => r.json())
      .then(
        (d: {
          data?: Array<{ code: string; label: string; polarity: string }>;
        }) => {
          setRunnerReasons({
            up: (d?.data ?? []).filter((r) => r.polarity === 'up'),
            down: (d?.data ?? []).filter((r) => r.polarity === 'down'),
          });
        }
      )
      .catch(() => {
        /* non-critical */
      });
  }, []);

  const submitRunnerFeedback = async (
    toolRunId: string,
    rating: 'up' | 'down',
    reasonCode?: string,
    comment?: string
  ) => {
    try {
      const res = await apiFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          tool_run_id: toolRunId,
          rating,
          ...(reasonCode ? { reason_code: reasonCode } : {}),
          ...(comment ? { comment } : {}),
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
        }),
      });
      const j = (await res.json().catch(() => null)) as {
        action_triggered?: 'reprocess' | 'admin_review' | null;
      } | null;
      return {
        actionTriggered: (j?.action_triggered ?? null) as
          | 'reprocess'
          | 'admin_review'
          | null,
      };
    } catch {
      return { actionTriggered: null };
    }
  };
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tools, loading: toolsLoading } = useTools();
  const { currentWorkspace } = useWorkspaces();
  const {
    executing,
    streamingOutput,
    result,
    error: executionError,
    executeTool,
    cancelExecution,
    resetExecution,
  } = useToolExecution();

  const [toolInput, setToolInput] = useState<Record<string, string>>({});
  const tool = tools.find((t) => t.slug === slug);
  const shouldAutoExecute = searchParams.get('execute') === 'true';

  // Resolve workspace: URL param → global selector
  const urlWorkspaceId = searchParams.get('workspace') ?? undefined;
  const workspaceId = urlWorkspaceId ?? currentWorkspace?.id ?? undefined;

  // Fetch workspace name for headers / breadcrumbs
  const { data: wsDetail } = useWorkspaceDetail(workspaceId);
  const workspaceName =
    wsDetail?.workspace?.name ?? currentWorkspace?.name ?? undefined;

  // Dynamic title: "On the Record | GovernGraph", falls back to slug while loading
  usePageTitle(tool?.name ?? (slug ? slug.replace(/-/g, ' ') : 'Tool'));

  const prevWorkspaceRef = useRef(workspaceId);

  useEffect(() => {
    const prev = prevWorkspaceRef.current;
    prevWorkspaceRef.current = workspaceId;
    if (workspaceId && prev && prev !== workspaceId) {
      if (!tool) {
        navigate(`/workspaces/${workspaceId}`, { replace: true });
      }
    }
  }, [workspaceId, tool, navigate]);

  const handleExecute = async () => {
    if (!tool) return;
    resetExecution();
    await executeTool({ toolId: tool.id, input: toolInput });
  };

  useEffect(() => {
    if (tool && shouldAutoExecute && Object.keys(toolInput).length === 0) {
      void handleExecute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, shouldAutoExecute]);

  const handleInputChange = (key: string, value: string) => {
    setToolInput((prev) => ({ ...prev, [key]: value }));
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (toolsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading tool...</p>
        </div>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────
  if (!tool) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Tool not found
        </h2>
        <p className="text-gray-600 mb-4">
          The tool you&apos;re looking for doesn&apos;t exist or you don&apos;t
          have access.
        </p>
        <Button onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const hasDedicatedUI = DEDICATED_SLUGS.has(tool.slug);

  // document-qa and on-the-record both own their full header
  const hidePageBack =
    tool.slug === 'document-qa' || tool.slug === 'meeting-summary';
  const hidePageHeader =
    tool.slug === 'on-the-record' ||
    tool.slug === 'document-qa' ||
    tool.slug === 'full-text-search' ||
    tool.slug === 'meeting-summary';

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hidePageHeader && (
        <div className="flex items-center gap-4">
          {!hidePageBack && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/tools')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{tool.name}</h1>
            {tool.description && (
              <p className="text-gray-600 mt-1">{tool.description}</p>
            )}
          </div>
        </div>
      )}

      {/* ── Dedicated UIs ──────────────────────────────────────────── */}
      {hasDedicatedUI && (
        <>
          {tool.slug === 'on-the-record' && (
            <AskTheCountyUI key={workspaceId} tool={tool} />
          )}
          {tool.slug === 'platform-guide' && (
            <PlatformGuideUI key={workspaceId} tool={tool} />
          )}
          {tool.slug === 'document-qa' && (
            <DocumentQaUI
              key={workspaceId}
              tool={tool}
              onBack={() => navigate('/tools')}
              workspaceName={workspaceName}
              workspaceId={workspaceId}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              preselectedArtifact={(location.state as any)?.preselectedArtifact}
            />
          )}
          {tool.slug === 'meeting-summary' && (
            <MeetingSummarizerUI key="meeting-summary" tool={tool} />
          )}
          {tool.slug === 'topic-monitor' && (
            <TopicMonitorUI key={workspaceId} />
          )}
          {tool.slug === 'deep-research-brief' && (
            <DeepResearchBriefUI key={workspaceId} tool={tool} />
          )}
          {tool.slug === 'full-text-search' && (
            <FullTextSearchUI key={workspaceId} tool={tool} />
          )}
        </>
      )}

      {/* ── Generic runner ─────────────────────────────────────────── */}
      {!hasDedicatedUI && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <Card>
            <CardHeader>
              <CardTitle>Input</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="prompt">Prompt</Label>
                <Input
                  id="prompt"
                  value={toolInput.prompt || ''}
                  onChange={(e) => handleInputChange('prompt', e.target.value)}
                  placeholder="Enter your prompt..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="context">Context (Optional)</Label>
                <Input
                  id="context"
                  value={toolInput.context || ''}
                  onChange={(e) => handleInputChange('context', e.target.value)}
                  placeholder="Additional context..."
                />
              </div>

              <Button
                onClick={handleExecute}
                disabled={executing}
                className="w-full"
              >
                {executing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Execute Tool
                  </>
                )}
              </Button>

              {executionError && (
                <div className="bg-red-50 text-red-800 p-3 rounded-md text-sm">
                  {executionError.message}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Output */}
          <StreamingResponse
            content={streamingOutput || result?.output?.toString() || ''}
            isStreaming={executing}
            onCancel={cancelExecution}
            title="Output"
          />
        </div>
      )}
      {!hasDedicatedUI && result?.id && !executing && (
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <MessageActions
            toolRunId={result.id}
            reasonsDown={runnerReasons.down}
            reasonsUp={runnerReasons.up}
            onFeedback={({ rating, reasonCode, comment }) =>
              submitRunnerFeedback(result.id, rating, reasonCode, comment)
            }
          />
        </div>
      )}
    </div>
  );
};
